import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { restServices } from 'src/app/rest.service';
import { Simulazione } from '../../models/entities/simulazione';
import { DominioEsposizioneNetta } from '../../models/entities/dominio-esposizione-netta';
import { DominioAggregazione } from '../../models/entities/dominio-aggregazione';
import { DominioValuta } from '../../models/entities/dominio-valuta';
import { DownloadExcelPrevisionaleInputDTO } from '../../models/entities/download-excel-previsionale-input-dto';
import { DownloadExcelVolumiDTO } from '../../models/entities/download-excel-volumi-dto';
import { DownloadExcelSimulazioneInputDTO } from '../../models/entities/download-excel-simulazione-input-dto';
import { DownloadExcelStrategieDTO } from '../../models/entities/download-excel-strategie-dto';
import { map } from 'rxjs/operators';
import { EsposizioneNetta } from '../../models/enum/esposizione-netta';
import { VolumiService } from 'src/app/views/simulativo/simulazione/volumi/volumi.service';
import { Forward } from '../../models/entities/forward';

type EntityResponseTypeSimulazioniArray = HttpResponse<Simulazione[]>;
type EntityResponseTypeSimulazione = HttpResponse<Simulazione>;

@Injectable()
export class SimulativoService {

  resourceUrl = environment.api_rischiocambio_url;
  resourceSimulazione = environment.api_rischiocambio_url + restServices.simulazione;

  constructor(
    private http: HttpClient,
    private volumiService: VolumiService
  ) { }

  public calcolaSimulazione(simulazione: Simulazione): Observable<EntityResponseTypeSimulazione> {
    return this.http.post<Simulazione>(`${this.resourceUrl}calcola-simulazione`, simulazione, { observe: 'response' });
  }

  deleteSimulazione(id: number): Observable<HttpResponse<{}>> {
    return this.http.delete(`${this.resourceSimulazione}/${id}`, { observe: 'response' });
  }

  downloadExcelPrevisionale(inputDto: DownloadExcelPrevisionaleInputDTO): Observable<any> {
    return this.http.post(`${this.resourceSimulazione}/download-excel-previsionale`, inputDto, { observe: 'response', responseType: 'arraybuffer' });
  }

  downloadExcelSimulazione(input: DownloadExcelSimulazioneInputDTO): Observable<any> {
    return this.http.post(`${this.resourceSimulazione}/download-excel-simulazione`, input, { observe: 'response', responseType: 'arraybuffer' });
  }

  downloadExcelStrategie(input: DownloadExcelStrategieDTO): Observable<any> {
    return this.http.post(`${this.resourceSimulazione}/download-excel-strategie`, input, { observe: 'response', responseType: 'arraybuffer' });
  }

  downloadExcelVolumi(input: DownloadExcelVolumiDTO): Observable<any> {
    return this.http.post(`${this.resourceSimulazione}/download-excel-volumi`, input, { observe: 'response', responseType: 'arraybuffer' });
  }

  getAllSimulazioni(): Observable<EntityResponseTypeSimulazioniArray> {
    return this.http.get<Simulazione[]>(this.resourceSimulazione, { observe: 'response' });
  }

  getTop5Simulazioni(): Observable<EntityResponseTypeSimulazioniArray> {
    return this.http.get<Simulazione[]>(`${this.resourceUrl}dashboard-simulazione`, { observe: 'response' });
  }

  getDominioAggregazione(): Observable<HttpResponse<DominioAggregazione[]>> {
    return this.http.get<DominioAggregazione[]>(this.resourceUrl + restServices.dominioAggregaziones, { observe: 'response' });
  }

  getDominioEsposizioneNetta(): Observable<HttpResponse<DominioEsposizioneNetta[]>> {
    return this.http.get<DominioEsposizioneNetta[]>(this.resourceUrl + restServices.dominioEsposizioneNettas, { observe: 'response' });
  }

  getDominioValutaEsposizione(): Observable<HttpResponse<DominioValuta[]>> {
    return this.http.get<DominioValuta[]>(this.resourceUrl + restServices.dominioValutas, { observe: 'response' });
  }

  getScenariByCross(cross: String): Observable<any> {
    return this.http.get<any>(this.resourceUrl + restServices.scenarios + '/' + cross, { observe: 'response' });
  }

  findSimulazioneById(id: number): Observable<EntityResponseTypeSimulazione> {
    return this.http.get<Simulazione>(`${this.resourceSimulazione}/${id}`, { observe: 'response' });
  }

  public inserisciSimulazione(simulazione: object): Observable<any> {
    return this.http.post<any>(`${this.resourceSimulazione}`, simulazione, { observe: 'response' });
  }

  uploadExcelVolumi(file: any): Observable<any> {
    return this.http.post<any>(`${this.resourceSimulazione}/upload-excel-volumi`, file, { observe: 'response' });
  }

  uploadExcelStrategie(file: any): Observable<any> {
    return this.http.post<any>(`${this.resourceSimulazione}/upload-excel-strategie`, file, { observe: 'response' });
  }

  downloadExcelEsposizioneNetta(input: DownloadExcelSimulazioneInputDTO): Observable<any> {
    return this.http.post(`${this.resourceSimulazione}/download-excel-esposizione-netta`, input, { observe: 'response', responseType: 'arraybuffer' });
  }

  // Utils per calcoli aggiuntivi simulazione
  calcolaCambioForwardChiusuraRischio(simulazione: Simulazione, forwards: Forward[]) {
    let volumeNettoTotale = 0;
    let volumeCambioATermineTotale = 0;
    simulazione.strategie.forEach(str => {
      const meseForward = this.volumiService.getMonthForForward(new Date(), new Date(str.dataFinePeriodo));
      const outright = this.volumiService.calcolaOutrightFromForwards({
        forwards: forwards,
        cambioSpot: simulazione.cambioSpot,
        meseForward
      });
      if (!outright) {
        return null;
      }
      const volumeComplessivoCoperto = str.volumeForward + str.volumeOpzioni + str.volumeCollar;
      const volumeNetto = str.volumePeriodo - volumeComplessivoCoperto;
      volumeNettoTotale += volumeNetto;
      const volumeCambioATermine = volumeNetto / outright;
      volumeCambioATermineTotale += volumeCambioATermine;
    });
    return volumeNettoTotale / volumeCambioATermineTotale;
  }
  
  calcolaCambioCopertureSpot(simulazione: Simulazione) {
    let cambioCopertureSpot = null;
    simulazione.outputSimulazione.forEach(os => {
      if (simulazione.cambioSpot === os.spot) {
        cambioCopertureSpot = os.cambioCoperture;
      }
    });
    return cambioCopertureSpot;
  }

  calcolaCambioGarantitoSuPercCoperta(simulazione: Simulazione) {
    let cambioGarantitoSuPercCoperta = 0;
    simulazione.outputSimulazione.forEach(os => {
      let cambioCoperture = Math.round(os.cambioCoperture * 10000) / 10000;
      if (simulazione.esposizioneNetta === EsposizioneNetta.IMPORT && (cambioCoperture < cambioGarantitoSuPercCoperta || cambioGarantitoSuPercCoperta === 0)) {
        cambioGarantitoSuPercCoperta = cambioCoperture;
      } else if (simulazione.esposizioneNetta === EsposizioneNetta.EXPORT && cambioCoperture > cambioGarantitoSuPercCoperta) {
        cambioGarantitoSuPercCoperta = cambioCoperture;
      }
    });
    return cambioGarantitoSuPercCoperta;
  }

  /* --- Cambio ottenibile su tutta l’esposizione in essere per la chiusura del rischio ---
       FORMULA: (esposizione coperta + esposizione non coperta) /
       (Esposizione coperta / cambio di copertura + esposizione non coperta / cambio forward medio per chiudere il rischio) */
  calcolaCambioInEssereChiusuraRischio(simulazione: Simulazione) {
    let cambioInEssereChiusuraRischio = simulazione.esposizioneComplessiva /
      (simulazione.esposizioneCoperta / simulazione.cambioGarantitoSuPercCoperta +
        simulazione.esposizioneNoncoperta / simulazione.cambioForwardChiusuraRischio);
    return cambioInEssereChiusuraRischio;
  }

  calcolaUtiliPerditeTotaliDaCoperture(simulazione: Simulazione) {
    let upTotaliDaCoperture = simulazione.esposizioneCoperta * (1 / simulazione.cambioSpot - 1 / simulazione.cambioCopertureSpot);
    if (simulazione.esposizioneNetta == EsposizioneNetta.EXPORT) {
      upTotaliDaCoperture *= -1;
    }
    return upTotaliDaCoperture;
  }

  calcolaUtiliPerditeEsposizioneResidua(simulazione: Simulazione) {
    let upEsposizioneResidua = simulazione.esposizioneNoncoperta * (1 / simulazione.cambioObiettivo - 1 / simulazione.cambioSpot);
    if (simulazione.esposizioneNetta == EsposizioneNetta.EXPORT) {
      upEsposizioneResidua *= -1;
    }
    return upEsposizioneResidua;
  }

  calcolaUtiliPerditeRealizzabiliEspResidua(simulazione: Simulazione) {
    let utiliPerditeRealizzabili = simulazione.esposizioneNoncoperta * (1 / simulazione.cambioObiettivo - 1 / simulazione.cambioForwardChiusuraRischio);
    if (simulazione.esposizioneNetta == EsposizioneNetta.EXPORT) {
      utiliPerditeRealizzabili *= -1;
    }
    return utiliPerditeRealizzabili;
  }

  calcolaCmpAttualeInteraEsposizione(simulazione: Simulazione) {
    let cmpAttualeInteraEsposizione = simulazione.esposizioneComplessiva /
      (simulazione.esposizioneCoperta / simulazione.cambioGarantitoSuPercCoperta + simulazione.esposizioneNoncoperta / simulazione.cambioSpot);
    return cmpAttualeInteraEsposizione;
  }

}